import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import { styled } from 'sensortower-components/src/utils/styled';
import ErrorBoundary from 'sensortower-components/src/base-components/ErrorBoundary';
import ContentModule from 'sensortower-components/src/base-components/ContentModule';
import { sidekick } from '../../utils/sidekick';
import { toJsonLd } from '../../utils/structuredData';
import { FrequentlyAskedQuestionsProps } from './FrequentlyAskedQuestions.types';
import { AccordionIcon } from './AccordionIcon';
import Head from 'next/head';
import { RichText } from '../../base-components/Text/Text.types';

function extractText(node: RichText['json']): string {
  let result = ' ';

  if (node.nodeType === 'text' || node.nodeType === 'paragraph') {
    return node.content?.map((innerContent: any) => innerContent.value).join('');
  }

  if (Array.isArray(node.content)) {
    for (const child of node.content) {
      result += extractText(child);
    }
  }

  return result;
}

export const FrequentlyAskedQuestions = ({ sidekickLookup, title, questionItem }: FrequentlyAskedQuestionsProps) => {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    headline: title,
    mainEntity: questionItem?.map((item) => ({
      '@type': 'Question',
      name: item.question || 'N/A',
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer?.json?.content?.map(extractText).join(' '),
      },
    })),
  };

  return (
    <ErrorBoundary>
      <Head>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: toJsonLd(faqSchema) }} />
      </Head>

      <Root {...sidekick(sidekickLookup)} data-testid="FrequentlyAskedQuestions">
        <Container maxWidth="xl">
          <Title variant="h2">{title}</Title>
          <AccordionWrapper>
            {questionItem?.map((item) => (
              <React.Fragment key={item.id}>
                <Accordion disableGutters elevation={0} data-testid={`FAQ-item-${item.id}`}>
                  <AccordionSummary
                    expandIcon={
                      <Box
                        sx={{
                          display: {
                            xs: 'none',
                            md: 'flex',
                          },
                        }}
                        data-testid="accordion-icon-wrapper"
                      >
                        <AccordionIcon />
                      </Box>
                    }
                    aria-controls={`panel-${item.id}-content`}
                    id={`panel-${item.id}-header`}
                    {...sidekick(item.sidekickLookup, 'question')}
                  >
                    <Question variant="h3">{item.question}</Question>
                  </AccordionSummary>
                  <AccordionDetails {...sidekick(item.sidekickLookup, 'answer')}>
                    <ContentModule __typename="Text" body={item.answer} variant="faq-answer" />
                  </AccordionDetails>
                </Accordion>

                <Divider orientation="horizontal" />
              </React.Fragment>
            ))}
          </AccordionWrapper>
        </Container>
      </Root>
    </ErrorBoundary>
  );
};

export default FrequentlyAskedQuestions;

const Root = styled(Box, {
  name: 'FrequentlyAskedQuestions',
  slot: 'Root',
})(({ theme }) => ({
  padding: theme.spacing(5, 0, 7.5),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(9, 0, 11),
  },
}));

const Title = styled(Typography, {
  name: 'FrequentlyAskedQuestions',
  slot: 'Title',
})(({ theme }) => ({
  fontWeight: '700',
  fontSize: '26px !important',
  lineHeight: 1.3,
  marginBottom: theme.spacing(3),
  maxWidth: '190px',
  [theme.breakpoints.up('md')]: {
    fontSize: '36px !important',
    marginBottom: '50px',
    maxWidth: '100%',
  },
}));

const AccordionWrapper = styled(Box, {
  name: 'FrequentlyAskedQuestions',
  slot: 'AccordionWrapper',
})(() => ({
  width: '100%',
}));

const Accordion = styled(MuiAccordion, {
  name: 'FrequentlyAskedQuestions',
  slot: 'Accordion',
})(() => ({
  backgroundColor: 'transparent',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: 0,
  },
}));

const AccordionSummary = styled(MuiAccordionSummary, {
  name: 'FrequentlyAskedQuestions',
  slot: 'AccordionSummary',
})({
  padding: 4,
});

const AccordionDetails = styled(MuiAccordionDetails, {
  name: 'FrequentlyAskedQuestions',
  slot: 'AccordionDetails',
})({
  padding: '0 0 6px 0',
});

const Question = styled(Typography, {
  name: 'FrequentlyAskedQuestions',
  slot: 'Question',
})(({ theme }) => ({
  fontWeight: 600,
  fontSize: '18px',
  fontStyle: 'normal',
  lineHeight: 1.5,
  color: theme.palette.text.primary,
  [theme.breakpoints.up('md')]: {
    fontSize: '28px',
    lineHeight: '33.6px',
  },
}));
